import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Users,
  Building2,
  Clock,
  CalendarX,
  Timer,
  Loader2,
} from "lucide-react";

const cards = [
  { key: "total_hours_today", label: "Horas Hoje", icon: Timer, suffix: " h", color: "text-primary" },
  { key: "active_now", label: "A Trabalhar Agora", icon: Clock, color: "text-emerald-400" },
  { key: "employees", label: "Funcionários", icon: Users, color: "text-sky-400" },
  { key: "obras_em_curso", label: "Obras em Curso", icon: Building2, color: "text-primary" },
  { key: "faltas_today", label: "Faltas Hoje", icon: CalendarX, color: "text-red-400" },
];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/time", { params: { start: new Date().toISOString().slice(0, 10) } }),
    ])
      .then(([s, t]) => {
        setStats(s.data);
        setEntries(t.data);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-7 h-7 text-primary animate-spin" />
      </div>
    );

  return (
    <div data-testid="dashboard-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
        Painel do Administrador
      </h1>
      <p className="text-muted-foreground mb-8">Visão geral de hoje.</p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {cards.map((c) => (
          <div
            key={c.key}
            data-testid={`stat-${c.key}`}
            className="bg-card border border-border rounded-xl p-5 hover:border-primary/40 transition-colors"
          >
            <c.icon className={`w-6 h-6 mb-3 ${c.color}`} />
            <p className="font-data text-3xl font-bold">
              {stats[c.key]}
              {c.suffix || ""}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="font-display font-semibold">Registos de hoje</h2>
        </div>
        {entries.length === 0 ? (
          <p className="p-8 text-center text-muted-foreground">Sem registos hoje.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b border-border">
                <tr>
                  <th className="px-5 py-3 font-medium">Funcionário</th>
                  <th className="px-5 py-3 font-medium">Obra</th>
                  <th className="px-5 py-3 font-medium">Entrada</th>
                  <th className="px-5 py-3 font-medium">Saída</th>
                  <th className="px-5 py-3 font-medium text-right">Horas</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-border/50 last:border-0">
                    <td className="px-5 py-3 font-medium">{e.user_name}</td>
                    <td className="px-5 py-3 text-muted-foreground">{e.obra_name || "—"}</td>
                    <td className="px-5 py-3 font-data">{fmt(e.clock_in)}</td>
                    <td className="px-5 py-3 font-data">
                      {e.clock_out ? (
                        fmt(e.clock_out)
                      ) : (
                        <span className="text-emerald-400">em curso</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right font-data font-semibold">
                      {e.hours || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
}
